import React, { useState, useMemo } from 'react';
import {
  Bell,
  User,
  Package,
  ShoppingCart,
  TrendingDown,
  AlertCircle,
  CreditCard,
  Settings,
  Megaphone,
  Filter,
  Search,
  // Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  useGetMyNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationsReadMutation,
} from '../../app/services/notificationApi';
import Loader from '../../Components/Ui/Loader';

/**
 * Map notification type to appropriate Lucide icon
 */
const getNotificationIcon = (type) => {
  const iconMap = {
    new_user: User,
    new_order: ShoppingCart,
    order_update: ShoppingCart,
    new_product: Package,
    product_update: Package,
    price_change: TrendingDown,
    low_stock: AlertCircle,
    payment_received: CreditCard,
    payment_pending: CreditCard,
    system: Settings,
    announcement: Megaphone,
  };
  return iconMap[type] || Bell;
};

/**
 * Format relative time from ISO date string
 */
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

const SellerNotifications = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Build query params based on filter
  const queryParams = useMemo(() => {
    const params = { page, limit };
    if (filter === 'unread') {
      params.unreadOnly = true;
    } else if (filter !== 'all') {
      params.type = filter;
    }
    return params;
  }, [filter, page]);

  // Fetch notifications
  const {
    data: notificationsData,
    isLoading,
    isFetching,
    error,
  } = useGetMyNotificationsQuery(queryParams);

  const [markAllNotificationsRead, { isLoading: isMarkingAll }] =
    useMarkAllNotificationsReadMutation();
  const [markNotificationsRead] = useMarkNotificationsReadMutation();

  const notifications = useMemo(() => notificationsData?.data || [], [notificationsData?.data]);
  const unreadCount = notificationsData?.unreadCount || 0;
  const pagination = notificationsData?.pagination || {};

  const filterOptions = [
    { value: 'all', label: 'All Notifications' },
    { value: 'unread', label: 'Unread' },
    { value: 'new_order', label: 'Orders' },
    { value: 'order_update', label: 'Order Updates' },
    { value: 'new_product', label: 'Products' },
    { value: 'price_change', label: 'Pricing' },
  ];

  // Client-side search filtering
  const filteredNotifications = useMemo(() => {
    if (!searchTerm) return notifications;
    const term = searchTerm.toLowerCase();
    return notifications.filter(
      (n) => n.title?.toLowerCase().includes(term) || n.message?.toLowerCase().includes(term),
    );
  }, [notifications, searchTerm]);

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      try {
        await markNotificationsRead({ notificationIds: [notification._id] });
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }
    // Navigate to action URL if present
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsRead();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(1);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Bell size={24} className="primary-color" />
          <h1 className="text-xl font-bold text-gray-800">Notifications</h1>
          {unreadCount > 0 && (
            <span className="primary-background white-text text-xs rounded-full px-2 py-1">
              {unreadCount} new
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            disabled={isMarkingAll}
            className="px-4 py-2 text-sm font-medium primary-color border border-primary rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isMarkingAll ? 'Marking...' : 'Mark All as Read'}
          </button>
        )}
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-500" />
          <select
            value={filter}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {filterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search
            size={16}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <span className="text-sm text-gray-500">
          {filteredNotifications.length} notification
          {filteredNotifications.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader />
          </div>
        ) : error ? (
          <div className="py-12 text-center">
            <AlertCircle size={48} className="mx-auto mb-4 text-red-400" />
            <h3 className="text-lg font-medium text-gray-800 mb-1">Failed to load notifications</h3>
            <p className="text-sm text-gray-500">Please try again later</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="py-12 text-center">
            <Bell size={48} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-800 mb-1">No notifications found</h3>
            <p className="text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search terms' : 'Check back later for updates'}
            </p>
          </div>
        ) : (
          <>
            {filteredNotifications.map((notification, index) => {
              const IconComponent = getNotificationIcon(notification.type);
              return (
                <div
                  key={notification._id}
                  className={`p-4 border-b border-gray-100 cursor-pointer transition-colors flex items-start gap-4 ${
                    !notification.isRead ? 'bg-orange-50' : 'hover:bg-gray-50'
                  } ${isFetching ? 'opacity-60' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div
                    className={`${
                      !notification.isRead ? 'primary-background' : 'bg-gray-400'
                    } rounded-full p-2 flex items-center justify-center`}
                  >
                    <IconComponent size={20} color="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <h3
                        className={`text-base ${
                          !notification.isRead ? 'font-semibold' : 'font-medium'
                        } text-gray-800`}
                      >
                        {notification.title}
                      </h3>
                      {!notification.isRead && (
                        <div className="w-2.5 h-2.5 primary-background rounded-full ml-2 mt-1" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{notification.message}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-400">
                        {formatRelativeTime(notification.createdAt)}
                      </span>
                      {notification.category && (
                        <span className="text-xs primary-color bg-orange-50 px-2 py-0.5 rounded-full capitalize">
                          {notification.category}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 p-4 border-t border-gray-100">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1 || isFetching}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages || isFetching}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SellerNotifications;
